import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Grid, Typography, Button, Chip, Card, CardContent, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Alert, Divider, LinearProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel, IconButton,
  Tooltip as MuiTooltip,
} from '@mui/material';
import {
  PlayArrow, Stop, TrendingUp, AccountBalance, ShowChart,
  SwapHoriz, EmojiEvents, Warning, CheckCircle, FiberManualRecord,
  Refresh, ShoppingCart, TrendingUp as TrendingUpIcon, Star, StarHalf, StarBorder,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { Link } from 'react-router-dom';
import dayjs from '../utils/dayjs';
import toast from 'react-hot-toast';
import StatCard from '../components/dashboard/StatCard';
import { dashboard, bot, trading, settings as settingsApi } from '../services/api';
import useStore from '../store/useStore';
import { useSocket } from '../hooks/useSocket';

const fmt = (v) => v !== undefined && v !== null ? parseFloat(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';
const pct = (v) => v !== undefined ? `${parseFloat(v).toFixed(1)}%` : '—';

function formatZerodhaContract(symbol, expiryDate, strike, type) {
  if (!symbol) return '';
  const cleanStrike = strike ? Math.round(parseFloat(strike)) : '';
  if (!expiryDate) {
    return `${symbol} ${cleanStrike} ${type || ''}`.trim();
  }
  const d = dayjs(expiryDate);
  const day = d.date();
  const month = d.format('MMM').toUpperCase();
  const suffix = (day === 1 || day === 21 || day === 31) ? 'st'
    : (day === 2 || day === 22) ? 'nd'
    : (day === 3 || day === 23) ? 'rd'
    : 'th';
  return `${symbol} ${day}${suffix} ${month} ${cleanStrike} ${type || ''}`.trim();
}

function PositionsMiniTable({ positions, emptyLabel }) {
  if (!positions || positions.length === 0)
    return <Typography color="text.secondary" variant="body2" sx={{ py: 2, textAlign: 'center' }}>{emptyLabel || 'No open positions'}</Typography>;
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ '& th': { fontWeight: 600, bgcolor: '#f5f5f5', py: 0.8 } }}>
            <TableCell>Contract</TableCell>
            <TableCell>Type</TableCell>
            <TableCell align="right">Strike</TableCell>
            <TableCell align="right">Entry</TableCell>
            <TableCell align="right">Current</TableCell>
            <TableCell align="right">P&L</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {positions.map(pos => {
            const pnl = parseFloat(pos.unrealized_pnl || 0);
            const entry = parseFloat(pos.entry_price || 0);
            const cur = parseFloat(pos.current_price || entry || 0);
            const pnlPct = entry > 0 ? ((cur - entry) / entry) * 100 : 0;
            const pnlColor = pnl >= 0 ? '#2e7d32' : '#c62828';
            const contractLabel = formatZerodhaContract(pos.symbol, pos.expiry_date, pos.strike_price, pos.instrument_type);
            const cleanStrike = pos.strike_price ? Math.round(parseFloat(pos.strike_price)) : '—';
            return (
              <TableRow key={pos.id} hover>
                <TableCell sx={{ fontWeight: 700, color: '#1e40af' }}>{contractLabel}</TableCell>
                <TableCell><Chip size="small" label={pos.instrument_type} color={pos.instrument_type === 'CE' ? 'success' : 'error'} /></TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>{cleanStrike}</TableCell>
                <TableCell align="right">{entry.toFixed(2)}</TableCell>
                <TableCell align="right">{cur.toFixed(2)}</TableCell>
                <TableCell align="right" sx={{ color: pnlColor, fontWeight: 700 }}>
                  {pnl >= 0 ? '+' : ''}{fmt(pnl)} ({pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(2)}%)
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function TradesMiniTable({ trades }) {
  if (!trades || trades.length === 0)
    return <Typography color="text.secondary" variant="body2" sx={{ py: 2, textAlign: 'center' }}>No trades yet</Typography>;
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ '& th': { fontWeight: 600, bgcolor: '#f5f5f5', py: 0.8 } }}>
            <TableCell>Date</TableCell>
            <TableCell>Contract</TableCell>
            <TableCell>Type</TableCell>
            <TableCell align="right">Entry</TableCell>
            <TableCell align="right">Exit</TableCell>
            <TableCell align="right">P&L</TableCell>
            <TableCell>Result</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {trades.map(t => {
            const pnl = parseFloat(t.net_pnl || 0);
            const entry = parseFloat(t.entry_price || 0);
            const exit = parseFloat(t.exit_price || entry || 0);
            const pnlPct = entry > 0 ? ((exit - entry) / entry) * 100 : (t.pnl_pct ? parseFloat(t.pnl_pct) : 0);
            const pnlColor = pnl >= 0 ? '#2e7d32' : '#c62828';
            const contractLabel = (t.instrument_type === 'EQUITY' || !t.expiry_date)
              ? `${t.symbol} (EQUITY)`
              : formatZerodhaContract(t.symbol, t.expiry_date, t.strike_price, t.instrument_type);
            return (
              <TableRow key={t.id} hover>
                <TableCell>{dayjs(t.trade_date).format('DD/MM')}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1e40af' }}>{contractLabel}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={t.instrument_type}
                    color={t.instrument_type === 'CE' ? 'success' : (t.instrument_type === 'PE' ? 'error' : 'primary')}
                  />
                </TableCell>
                <TableCell align="right">{entry.toFixed(2)}</TableCell>
                <TableCell align="right">{exit.toFixed(2)}</TableCell>
                <TableCell align="right" sx={{ color: pnlColor, fontWeight: 700 }}>
                  {pnl >= 0 ? '+' : ''}{fmt(pnl)} ({pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(2)}%)
                </TableCell>
                <TableCell><Chip size="small" label={t.trade_result} color={t.trade_result === 'WIN' ? 'success' : t.trade_result === 'LOSS' ? 'error' : 'default'} /></TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function DashboardPage() {
  const { setBotStatus, setDashboardData } = useStore();
  const [data, setData] = useState(null);
  const [chart, setChart] = useState([]);
  const [liveTrades, setLiveTrades] = useState([]);
  const [paperTrades, setPaperTrades] = useState([]);
  const [signalLog, setSignalLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [botLoading, setBotLoading] = useState(false);
  const [marketData, setMarketData] = useState({});
  const [recommendations, setRecommendations] = useState([]);
  const [recsStatus, setRecsStatus] = useState({ status: '', message: '' });
  const [recsLoading, setRecsLoading] = useState(false);
  const [buyDialog, setBuyDialog] = useState({ open: false, stock: null });
  const [buyQty, setBuyQty] = useState(1);
  const [buyProduct, setBuyProduct] = useState('CNC');
  const [buyLoading, setBuyLoading] = useState(false);
  const [equityOrders, setEquityOrders] = useState([]);
  const [capitalSummary, setCapitalSummary] = useState(null);
  const [sellLoadingId, setSellLoadingId] = useState(null);
  const [reasoningDialog, setReasoningDialog] = useState({ open: false, text: '', symbol: '' });
  const [testBuyDialog, setTestBuyDialog] = useState({ open: false, symbol: '', direction: '' });
  const [testBuyLoading, setTestBuyLoading] = useState(false);
  const [screenerAutoTrade, setScreenerAutoTrade] = useState(null); // { enabled, capital, minScore }

  const loadData = useCallback(async () => {
    try {
      const dashRes  = await dashboard.get();
      const dashData = dashRes.data.data;
      const mode     = dashData?.mode || 'PAPER';

      setData(dashData);
      setDashboardData(dashData);
      setBotStatus(dashData?.botStatus);

      const results = await Promise.allSettled([
        dashboard.pnlChart({ days: 30, mode }),
        dashboard.recentTrades({ limit: 8, mode: 'LIVE' }),
        dashboard.recentTrades({ limit: 8, mode: 'PAPER' }),
        trading.signalLog(),
        trading.marketData({ symbol: 'NIFTY' }),
        trading.marketData({ symbol: 'BANKNIFTY' }),
      ]);

      setChart(results[0].status === 'fulfilled' ? results[0].value?.data?.data || [] : []);
      setLiveTrades(results[1].status === 'fulfilled' ? results[1].value?.data?.data || [] : []);
      setPaperTrades(results[2].status === 'fulfilled' ? results[2].value?.data?.data || [] : []);
      setSignalLog(results[3].status === 'fulfilled' ? results[3].value?.data?.data || [] : []);
      
      const niftyData = results[4].status === 'fulfilled' ? results[4].value?.data?.data : null;
      const bankData  = results[5].status === 'fulfilled' ? results[5].value?.data?.data : null;
      setMarketData({ NIFTY: niftyData, BANKNIFTY: bankData });

      // Non-blocking: recommendations + equity orders
      trading.stockRecommendations().then(r => {
        setRecommendations(r.data.data || []);
        setRecsStatus({ status: r.data.status || '', message: r.data.message || '' });
      }).catch(() => {});
      settingsApi.getAll().then(r => {
        const s = r.data.data || {};
        setScreenerAutoTrade({
          enabled: s.stock_screener_auto_trade_enabled === true || s.stock_screener_auto_trade_enabled === 'true',
          capital: s.stock_screener_capital,
          minScore: s.stock_screener_min_score,
        });
      }).catch(() => {});
      // Only show running orders for the bot's CURRENT mode — a PAPER bot
      // shouldn't surface real LIVE holdings (or vice versa) on the dashboard.
      trading.equityOrders({ status: 'OPEN', mode }).then(r => setEquityOrders(r.data.data || [])).catch(() => {});
      trading.capitalSummary().then(r => setCapitalSummary(r.data)).catch(() => {});
    } catch (err) {
      // Surface this — silently swallowing it here is exactly why a failed refresh
      // after Start/Stop looks like "nothing happened" with no diagnostic trail.
      console.error('Dashboard refresh failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    // When market is closed, relax refresh interval to 60s to reduce unnecessary network requests
    const intervalMs = (data && !data.marketOpen) ? 60000 : 30000;
    const t = setInterval(loadData, intervalMs);
    return () => clearInterval(t);
  }, [loadData, data?.marketOpen]);

  // Re-fetch running equity orders for the bot's CURRENT mode only — used after
  // buy/sell/activate actions so the dashboard list never mixes PAPER and LIVE.
  const refreshEquityOrders = useCallback(() => {
    trading.equityOrders({ status: 'OPEN', mode: data?.mode || 'PAPER' })
      .then(r => setEquityOrders(r.data.data || [])).catch(() => {});
  }, [data?.mode]);

  const handleBotAction = async (action) => {
    setBotLoading(true);
    try {
      if (action === 'start') { await bot.start(); toast.success('Bot started!'); }
      else { await bot.stop(); toast.success('Bot stopped'); }
      await loadData();
    } catch { /* handled */ } finally { setBotLoading(false); }
  };

  const loadRecommendations = async (forceRefresh = false) => {
    setRecsLoading(true);
    try {
      const r = await trading.stockRecommendations(forceRefresh ? { refresh: 1 } : {});
      setRecommendations(r.data.data || []);
      setRecsStatus({ status: r.data.status || '', message: r.data.message || '' });
    } catch { /* handled */ } finally { setRecsLoading(false); }
  };

  const openBuyDialog = (stock) => {
    const defaultQty = Math.max(1, Math.floor(10000 / stock.ltp));
    setBuyQty(defaultQty);
    setBuyProduct('CNC');
    setBuyDialog({ open: true, stock });
  };

  const handleBuy = async () => {
    const { stock } = buyDialog;
    setBuyLoading(true);
    try {
      const res = await trading.buyStock({
        symbol: stock.symbol, name: stock.name,
        quantity: buyQty, ltp: stock.ltp, product: buyProduct,
      });
      toast.success(res.data.message || 'Order placed!');
      setBuyDialog({ open: false, stock: null });
      refreshEquityOrders();
    } catch { /* toast shown by interceptor */ } finally { setBuyLoading(false); }
  };

  const handleSell = async (order) => {
    const warning = order.mode === 'PAPER'
      ? `Sell ${order.quantity} × ${order.symbol} at market? This is a simulated PAPER trade — no real order is sent.`
      : `Sell ${order.quantity} × ${order.symbol} at market? This places a REAL Zerodha order.`;
    if (!window.confirm(warning)) return;
    setSellLoadingId(order.id);
    try {
      const res = await trading.sellStock({ id: order.id });
      toast.success(res.data.message || 'Sell order placed!');
      refreshEquityOrders();
    } catch { /* toast shown by interceptor */ } finally { setSellLoadingId(null); }
  };

  const handleActivateMonitoring = async (order) => {
    if (!window.confirm(`Activate trailing-SL monitoring for ${order.symbol}? The stop-loss will be set relative to TODAY'S live price, not your original purchase price.`)) return;
    setSellLoadingId(order.id);
    try {
      const res = await trading.activateEquityMonitoring(order.id);
      toast.success(res.data.message || 'Monitoring activated');
      refreshEquityOrders();
    } catch { /* toast shown by interceptor */ } finally { setSellLoadingId(null); }
  };

  const handleTestBuy = async () => {
    setTestBuyLoading(true);
    try {
      const res = await trading.manualBuy({ symbol: testBuyDialog.symbol, direction: testBuyDialog.direction });
      toast.success(res.data.message || 'Test order placed');
      setTestBuyDialog({ open: false, symbol: '', direction: '' });
      await loadData();
    } catch { /* toast shown by interceptor */ } finally { setTestBuyLoading(false); }
  };

  const isRunning  = data?.isRunning || data?.botStatus?.is_running;
  const live       = data?.live  || {};
  const paper      = data?.paper || {};
  const todayPnl   = parseFloat(data?.todayPnl || 0);
  const weekPnl    = parseFloat(data?.weekPnl   || 0);
  const monthPnl   = parseFloat(data?.monthPnl  || 0);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress size={60} /></Box>;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Trading Dashboard</Typography>
          <Typography variant="body2" color="text.secondary">
            Active Mode: <b>{data?.mode || 'PAPER'}</b> | Market: {data?.marketOpen ? '🟢 Open' : '🔴 Closed'}
          </Typography>
        </Box>
        <Button
          variant="contained" color={isRunning ? 'error' : 'success'}
          startIcon={isRunning ? <Stop /> : <PlayArrow />}
          onClick={() => handleBotAction(isRunning ? 'stop' : 'start')}
          disabled={botLoading}
        >
          {botLoading ? <CircularProgress size={20} color="inherit" /> : isRunning ? 'Stop Bot' : 'Start Bot'}
        </Button>
      </Box>

      {data?.botStatus?.consecutive_losses >= 2 && (
        <Alert severity="warning" sx={{ mb: 2 }} icon={<Warning />}>
          {data.botStatus.consecutive_losses} consecutive losses. Bot will stop after {3 - data.botStatus.consecutive_losses} more.
        </Alert>
      )}

      {/* Market Prices */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {['NIFTY', 'BANKNIFTY'].map(sym => (
          <Grid item xs={12} sm={6} key={sym}>
            <Card sx={{
              borderRadius: 3.5,
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              color: '#ffffff',
              border: '1px solid #334155',
              boxShadow: '0 4px 12px rgba(15, 23, 42, 0.12)'
            }}>
              <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 800, letterSpacing: '0.06em' }}>
                    {sym} INDEX
                  </Typography>
                  <Chip
                    label={data?.marketOpen ? 'LIVE MARKET' : 'LAST CLOSE'}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.65rem',
                      fontWeight: 800,
                      bgcolor: data?.marketOpen ? 'rgba(16, 185, 129, 0.2)' : 'rgba(148, 163, 184, 0.2)',
                      color: data?.marketOpen ? '#34d399' : '#cbd5e1',
                      border: `1px solid ${data?.marketOpen ? 'rgba(16, 185, 129, 0.4)' : 'rgba(148, 163, 184, 0.3)'}`
                    }}
                  />
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#ffffff', letterSpacing: '-0.02em', my: 0.5 }}>
                  {marketData[sym]?.spot ? parseFloat(marketData[sym].spot).toLocaleString('en-IN') : '—'}
                </Typography>
                <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>
                  Real-time NSE Spot Feed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Capital Cards — Live vs Paper */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6}>
          <Card sx={{ borderRadius: 2, border: '2px solid #f44336' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Live Capital (Zerodha)</Typography>
                  <Typography variant="h5" fontWeight={700} color="error.main">{fmt(live.capital)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Today: <b style={{ color: (live.todayPnl || 0) >= 0 ? '#2e7d32' : '#c62828' }}>{fmt(live.todayPnl || 0)}</b>
                    &nbsp;·&nbsp;{live.todayTrades || 0} trades
                  </Typography>
                </Box>
                <Chip label="LIVE" color="error" size="small" sx={{ fontWeight: 700 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card sx={{ borderRadius: 2, border: '2px solid #1976d2' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Paper Cash Available (Net Worth: {fmt(capitalSummary?.paper?.netWorth || 100000)})</Typography>
                  <Typography variant="h5" fontWeight={700} color="primary.main">{fmt(paper.capital)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Today P&amp;L: <b style={{ color: (paper.todayPnl || 0) >= 0 ? '#2e7d32' : '#c62828' }}>{fmt(paper.todayPnl || 0)}</b>
                    &nbsp;·&nbsp;{paper.todayTrades || 0} trades
                  </Typography>
                </Box>
                <Chip label="PAPER" color="primary" size="small" sx={{ fontWeight: 700 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Capital Wallet — full money tracker */}
      {capitalSummary && (
        <Card sx={{ borderRadius: 2, mb: 2, border: '1px solid #e3f2fd' }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>Capital Wallet</Typography>
            <Grid container spacing={2}>
              {/* Paper wallet */}
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 1.5, bgcolor: '#e3f2fd', borderRadius: 1.5 }}>
                  <Typography variant="subtitle2" fontWeight={700} color="primary" gutterBottom>PAPER Wallet</Typography>
                  <Grid container spacing={1}>
                    {[
                      { label: 'Initial Capital',    value: fmt(capitalSummary.paper?.initial) },
                      { label: 'Available Cash',     value: fmt(capitalSummary.paper?.availableCash),    bold: true, color: '#1565c0' },
                      { label: 'Equity Deployed',    value: fmt(capitalSummary.paper?.equityDeployed),   color: '#e65100' },
                      { label: 'Net Worth',          value: fmt(capitalSummary.paper?.netWorth),         bold: true },
                      { label: 'Equity P&L',        value: fmt(capitalSummary.paper?.equityPnl),        color: capitalSummary.paper?.equityPnl >= 0 ? '#2e7d32' : '#c62828' },
                      { label: 'Options P&L',       value: fmt(capitalSummary.paper?.optionsPnl),       color: capitalSummary.paper?.optionsPnl >= 0 ? '#2e7d32' : '#c62828' },
                      { label: 'Charges Paid',       value: fmt(capitalSummary.paper?.totalCharges),     color: '#6d4c41' },
                    ].map(({ label, value, bold, color }) => (
                      <Grid item xs={6} key={label}>
                        <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
                        <Typography variant="body2" fontWeight={bold ? 700 : 500} sx={{ color: color || 'text.primary' }}>{value}</Typography>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Grid>
              {/* Live wallet */}
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 1.5, bgcolor: '#fce4ec', borderRadius: 1.5 }}>
                  <Typography variant="subtitle2" fontWeight={700} color="error" gutterBottom>LIVE Wallet</Typography>
                  <Grid container spacing={1}>
                    {[
                      { label: 'Configured Capital', value: fmt(capitalSummary.live?.configured) },
                      { label: 'Equity Deployed',    value: fmt(capitalSummary.live?.equityDeployed),   color: '#e65100' },
                      { label: 'Available (Est.)',   value: fmt(capitalSummary.live?.availableEst),     bold: true, color: '#1b5e20' },
                      { label: 'Equity P&L',        value: fmt(capitalSummary.live?.equityPnl),        color: capitalSummary.live?.equityPnl >= 0 ? '#2e7d32' : '#c62828' },
                      { label: 'Charges Paid',       value: fmt(capitalSummary.live?.totalCharges),     color: '#6d4c41' },
                      capitalSummary.live?.zerodha
                        ? { label: 'Zerodha Balance', value: fmt(capitalSummary.live.zerodha.availableCash), bold: true, color: '#c62828' }
                        : { label: 'Zerodha Balance', value: 'Not connected', color: '#9e9e9e' },
                    ].map(({ label, value, bold, color }) => (
                      <Grid item xs={6} key={label}>
                        <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
                        <Typography variant="body2" fontWeight={bold ? 700 : 500} sx={{ color: color || 'text.primary' }}>{value}</Typography>
                      </Grid>
                    ))}
                  </Grid>
                  {capitalSummary.live?.zerodha && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Zerodha: Opening ₹{parseFloat(capitalSummary.live.zerodha.openingBalance || 0).toLocaleString('en-IN')} · Collateral ₹{parseFloat(capitalSummary.live.zerodha.collateral || 0).toLocaleString('en-IN')}
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Charges = STT + Exchange fees + GST + Stamp duty (Zerodha CNC delivery rates). Updates every 30s.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Active-mode Stats */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3}><StatCard title="Today's P&L" value={fmt(todayPnl)} icon={<ShowChart />} color="auto" trend={todayPnl} subtitle={`${data?.todayTrades || 0} trades · ${data?.mode}`} /></Grid>
        <Grid item xs={6} sm={3}><StatCard title="Week P&L" value={fmt(weekPnl)} icon={<TrendingUp />} color="auto" trend={weekPnl} subtitle="Last 7 days" /></Grid>
        <Grid item xs={6} sm={3}><StatCard title="Month P&L" value={fmt(monthPnl)} icon={<TrendingUp />} color="auto" trend={monthPnl} subtitle="This month" /></Grid>
        <Grid item xs={6} sm={3}><StatCard title="Open Positions" value={(data?.openPositions?.length || 0) + ' / 2'} icon={<SwapHoriz />} color="#7b1fa2" subtitle={`${data?.mode} mode`} /></Grid>
      </Grid>

      {/* Bot Status */}
      <Card sx={{ borderRadius: 2, mb: 2 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>Bot Status</Typography>
          <Grid container spacing={2}>
            {[
              { label: 'Status',       value: <Chip size="small" icon={<FiberManualRecord sx={{ fontSize: '10px !important' }} />} label={isRunning ? 'Running' : 'Stopped'} color={isRunning ? 'success' : 'default'} /> },
              { label: 'Active Mode',  value: <Chip size="small" label={data?.mode} color={data?.mode === 'LIVE' ? 'error' : 'info'} /> },
              { label: 'Trades Today', value: <b>{data?.todayTrades || 0}</b> },
              { label: 'Consec. Losses', value: <b style={{ color: data?.botStatus?.consecutive_losses >= 2 ? '#c62828' : 'inherit' }}>{data?.botStatus?.consecutive_losses || 0} / 3</b> },
            ].map(({ label, value }) => (
              <Grid item xs={6} sm={3} key={label}>
                <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
                <Box sx={{ mt: 0.5 }}>{value}</Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Open Positions — Live and Paper side by side */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 2, border: '1px solid #ffcdd2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h6" fontWeight={600}>Live Positions</Typography>
                <Chip size="small" label={`${(live.openPositions || []).length} open`} color="error" />
              </Box>
              <PositionsMiniTable positions={live.openPositions} emptyLabel="No live positions" />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 2, border: '1px solid #bbdefb' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h6" fontWeight={600}>Paper Positions</Typography>
                <Chip size="small" label={`${(paper.openPositions || []).length} open`} color="primary" />
              </Box>
              <PositionsMiniTable positions={paper.openPositions} emptyLabel="No paper positions" />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* P&L Chart */}
      <Card sx={{ borderRadius: 2, mb: 2 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>P&L Chart — {data?.mode} (30 Days)</Typography>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={chart} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tickFormatter={v => dayjs(v).format('DD/MM')} tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={v => `₹${v}`} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => [`₹${parseFloat(v).toFixed(2)}`, '']} labelFormatter={v => dayjs(v).format('DD MMM YYYY')} />
              <ReferenceLine y={0} stroke="#999" strokeDasharray="3 3" />
              <Line type="monotone" dataKey="cumulativePnl" stroke="#1976D2" strokeWidth={2.5} dot={false} name="Cumulative P&L" />
              <Line type="monotone" dataKey="dailyPnl" stroke="#42a5f5" strokeWidth={1.5} dot={{ r: 3 }} strokeDasharray="5 5" name="Daily P&L" />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Recent Trades — Live and Paper side by side */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 2, border: '1px solid #ffcdd2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h6" fontWeight={600}>Recent Live Trades</Typography>
                <Chip size="small" label="LIVE" color="error" />
              </Box>
              <TradesMiniTable trades={liveTrades} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 2, border: '1px solid #bbdefb' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h6" fontWeight={600}>Recent Paper Trades</Typography>
                <Chip size="small" label="PAPER" color="primary" />
              </Box>
              <TradesMiniTable trades={paperTrades} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ── Stock Recommendations ─────────────────────────────────────────── */}
      <Card sx={{ borderRadius: 2, mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUpIcon color="success" /> NSE Stock Recommendations
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Real-time price action scoring · NIFTY 50 stocks · Updates every 5 min
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {screenerAutoTrade && (
                <MuiTooltip title={
                  screenerAutoTrade.enabled
                    ? `Auto buy/sell is ON — STRONG BUY stocks (score ≥ ${screenerAutoTrade.minScore || 75}) are auto-purchased against ₹${parseFloat(screenerAutoTrade.capital || 0).toLocaleString('en-IN')} allocated capital. Configure in Settings → Trading.`
                    : 'Auto buy/sell is OFF — recommendations are informational only, click Buy to act on one. Enable in Settings → Trading.'
                }>
                  <Chip
                    component={Link} to="/settings" clickable
                    size="small"
                    label={screenerAutoTrade.enabled ? '🤖 Auto-Trade: ON' : '🤖 Auto-Trade: OFF'}
                    sx={{
                      fontWeight: 700,
                      bgcolor: screenerAutoTrade.enabled ? '#e8f5e9' : '#f5f5f5',
                      color: screenerAutoTrade.enabled ? '#2e7d32' : 'text.secondary',
                    }}
                  />
                </MuiTooltip>
              )}
              <Button
                size="small" variant="outlined" startIcon={recsLoading ? <CircularProgress size={14} /> : <Refresh />}
                onClick={() => loadRecommendations(true)} disabled={recsLoading}
              >
                Refresh
              </Button>
            </Box>
          </Box>

          {recommendations.length === 0 && !recsLoading && (() => {
            const { status, message } = recsStatus;
            const cfg = {
              TOKEN_EXPIRED: { severity: 'error',   icon: '🔑', text: message || 'Zerodha token expired — go to Settings → Zerodha and reconnect.' },
              MARKET_CLOSED: { severity: 'warning', icon: '🕐', text: message || 'Market is closed. Recommendations appear after 9:15 AM on trading days.' },
              NO_SIGNALS:    { severity: 'info',    icon: '📊', text: message || 'No stocks meeting buy criteria right now — market may be consolidating.' },
            }[status] || { severity: 'info', icon: '⏳', text: 'Loading data — click Refresh or wait for market to open.' };
            return (
              <Alert severity={cfg.severity} sx={{ mb: 1 }}>
                {cfg.icon}&nbsp; {cfg.text}
              </Alert>
            );
          })()}

          {recommendations.length > 0 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: '#f5f5f5', py: 1 } }}>
                    <TableCell>#</TableCell>
                    <TableCell>Stock</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell align="right">Change</TableCell>
                    <TableCell align="right">Intraday</TableCell>
                    <TableCell align="center">Score</TableCell>
                    <TableCell align="center">
                      Upside %
                      <MuiTooltip title="Heuristic confidence based on momentum factors — not a guarantee. Capped at 85% since no strategy is ever certain.">
                        <span style={{ cursor: 'help' }}> ⓘ</span>
                      </MuiTooltip>
                    </TableCell>
                    <TableCell align="center">Rating</TableCell>
                    <TableCell>Top Reason</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recommendations.map((s, idx) => {
                    const isStrongBuy = s.rating === 'STRONG BUY';
                    const isBuy      = s.rating === 'BUY';
                    const ratingColor = isStrongBuy ? '#2e7d32' : isBuy ? '#1565c0' : '#e65100';
                    const ratingBg    = isStrongBuy ? '#e8f5e9' : isBuy ? '#e3f2fd' : '#fff8e1';
                    const changeColor = s.changePct >= 0 ? '#2e7d32' : '#c62828';
                    const intColor    = s.intraDayPct >= 0 ? '#2e7d32' : '#c62828';
                    const reasonsList = Array.isArray(s.reasons)
                      ? s.reasons
                      : (typeof s.reasons === 'string'
                          ? (() => { try { const p = JSON.parse(s.reasons); return Array.isArray(p) ? p : [s.reasons]; } catch { return [s.reasons]; } })()
                          : []);

                    return (
                      <TableRow key={s.symbol} hover
                        sx={{ '& td': { py: 0.8 }, bgcolor: isStrongBuy ? '#f1f8e9' : 'inherit' }}>
                        <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>{idx + 1}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                            <Typography fontWeight={700} variant="body2">{s.symbol}</Typography>
                            {s.pe && (
                              <Chip
                                size="small"
                                label={`P/E ${s.pe}`}
                                color={s.pe > 50 ? 'warning' : s.pe <= 22 ? 'success' : 'default'}
                                variant={s.pe > 50 ? 'filled' : 'outlined'}
                                sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600 }}
                                title={s.pe > 50 ? 'P/E > 50: Zerodha critical scrip warning' : 'Safe/Moderate valuation'}
                              />
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary" noWrap>{s.name}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={600}>{s.ltp?.toLocaleString('en-IN')}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600} color={changeColor}>
                            {s.changePct >= 0 ? '+' : ''}{s.changePct?.toFixed(2)}%
                          </Typography>
                          <Typography variant="caption" color={changeColor}>
                            {s.changeAmt >= 0 ? '+' : ''}{s.changeAmt?.toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color={intColor}>
                            {s.intraDayPct >= 0 ? '+' : ''}{s.intraDayPct?.toFixed(2)}%
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 50 }}>
                              <LinearProgress
                                variant="determinate" value={s.score}
                                sx={{
                                  height: 6, borderRadius: 3,
                                  bgcolor: '#e0e0e0',
                                  '& .MuiLinearProgress-bar': {
                                    bgcolor: s.score >= 75 ? '#2e7d32' : s.score >= 55 ? '#1565c0' : '#e65100',
                                  },
                                }}
                              />
                            </Box>
                            <Typography variant="caption" fontWeight={700} sx={{ minWidth: 24 }}>{s.score}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={700}
                            color={s.probabilityPct >= 65 ? '#2e7d32' : s.probabilityPct >= 50 ? '#1565c0' : '#e65100'}>
                            {s.probabilityPct}%
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small" label={s.rating}
                            sx={{ bgcolor: ratingBg, color: ratingColor, fontWeight: 700, fontSize: 11 }}
                            icon={isStrongBuy ? <Star sx={{ fontSize: '14px !important', color: ratingColor + ' !important' }} /> : undefined}
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 220 }}>
                          <MuiTooltip
                            title={
                              <Box>
                                {reasonsList.map((r, i) => (
                                  <Typography key={i} variant="caption" sx={{ display: 'block' }}>{r}</Typography>
                                ))}
                              </Box>
                            }
                          >
                            <Box sx={{ cursor: 'help' }}>
                              {s.newsMatched && (
                                <Chip size="small" label="📰 News"
                                  sx={{ mr: 0.5, fontSize: 10, height: 18,
                                        bgcolor: s.newsAdjustment > 0 ? '#e8f5e9' : '#fce4ec',
                                        color: s.newsAdjustment > 0 ? '#2e7d32' : '#c62828' }} />
                              )}
                              <Typography variant="caption" color="text.secondary" noWrap component="span">
                                {reasonsList[0] || '—'}
                              </Typography>
                            </Box>
                          </MuiTooltip>
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            size="small" variant="contained" color="success"
                            startIcon={<ShoppingCart sx={{ fontSize: 14 }} />}
                            onClick={() => openBuyDialog(s)}
                            sx={{ fontSize: 11, py: 0.4, px: 1.2, minWidth: 60 }}
                          >
                            Buy
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* ── Buy Stock Dialog ───────────────────────────────────────────────── */}
      <Dialog open={buyDialog.open} onClose={() => !buyLoading && setBuyDialog({ open: false, stock: null })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShoppingCart color="success" />
            Buy {buyDialog.stock?.symbol}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">{buyDialog.stock?.name}</Typography>
            <Typography variant="h5" fontWeight={700} color="success.main">
              {buyDialog.stock?.ltp?.toLocaleString('en-IN')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Change: <b style={{ color: (buyDialog.stock?.changePct || 0) >= 0 ? '#2e7d32' : '#c62828' }}>
                {(buyDialog.stock?.changePct || 0) >= 0 ? '+' : ''}{buyDialog.stock?.changePct?.toFixed(2)}%
              </b>
              &nbsp;·&nbsp;Score: <b>{buyDialog.stock?.score}/100</b>
              &nbsp;·&nbsp;<Chip size="small" label={buyDialog.stock?.rating} sx={{ fontSize: 10, height: 18 }} />
            </Typography>
          </Box>

          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid item xs={6}>
              <TextField
                label="Quantity (shares)" type="number" fullWidth size="small"
                value={buyQty}
                onChange={e => setBuyQty(Math.max(1, parseInt(e.target.value) || 1))}
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Product</InputLabel>
                <Select value={buyProduct} label="Product" onChange={e => setBuyProduct(e.target.value)}>
                  <MenuItem value="CNC">CNC — Delivery (recommended)</MenuItem>
                  <MenuItem value="MIS">MIS — Intraday (auto-sold at 3:20 PM)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box sx={{ bgcolor: '#f5f5f5', borderRadius: 1, p: 1.5, mb: 1.5 }}>
            <Typography variant="body2">
              <b>Estimated Value:</b>&nbsp;
              {((buyDialog.stock?.ltp || 0) * buyQty).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
            <Typography variant="caption" color={buyProduct === 'MIS' ? 'error' : 'text.secondary'} display="block">
              {buyProduct === 'CNC' ? '✅ CNC Delivery — shares go into your demat, no auto square-off' : '⚠️ MIS Intraday — Zerodha will AUTO-SELL these shares at 3:20 PM today'}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              🛡️ Auto Stop-Loss enabled — trails up as price rises, locks to breakeven at +20% profit, auto-sells on trigger
            </Typography>
          </Box>

          {data?.mode === 'PAPER' ? (
            <Alert severity="info" sx={{ fontSize: 13 }}>
              Bot is in <b>PAPER</b> mode — this will be a <b>simulated</b> trade only.
              No real order is sent to Zerodha. Switch the bot to LIVE to place real orders.
            </Alert>
          ) : (
            <Alert severity="error" sx={{ fontSize: 13 }}>
              Bot is in <b>LIVE</b> mode — this places a <b>REAL market order</b> in your Zerodha account immediately.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setBuyDialog({ open: false, stock: null })} disabled={buyLoading}>Cancel</Button>
          <Button
            variant="contained" color="success" onClick={handleBuy}
            disabled={buyLoading} startIcon={buyLoading ? <CircularProgress size={16} /> : <ShoppingCart />}
          >
            {buyLoading ? 'Placing…' : `Confirm Buy ${buyQty} × ${buyDialog.stock?.symbol || ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Equity Stock Orders (running only — see Equity Orders page for full history) ── */}
      {equityOrders.length > 0 && (
        <Card sx={{ borderRadius: 2, mb: 2, border: '1px solid #c8e6c9' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <ShoppingCart color="success" fontSize="small" />
              <Typography variant="h6" fontWeight={700}>Running Equity Stock Orders</Typography>
              <Chip size="small" label={`${equityOrders.length} running`} color="success" />
              <Chip size="small" label={data?.mode === 'LIVE' ? 'LIVE' : 'PAPER'}
                sx={{ bgcolor: data?.mode === 'LIVE' ? '#fce4ec' : '#e3f2fd',
                      color: data?.mode === 'LIVE' ? '#c2185b' : '#1565c0',
                      fontWeight: 700 }} />
              <Button size="small" component={Link} to="/equity-orders" sx={{ ml: 'auto' }}>
                View All Orders
              </Button>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: '#f1f8e9', py: 0.8 } }}>
                    <TableCell>Date &amp; Time</TableCell>
                    <TableCell>Stock</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Buy Price</TableCell>
                    <TableCell align="right">Current</TableCell>
                    <TableCell align="right">
                      Stop Loss
                      <MuiTooltip title="Auto step-trailing: ratchets up as price rises, locks to breakeven at +20% profit. Auto-sells when price falls to this level.">
                        <span style={{ cursor: 'help' }}> ⓘ</span>
                      </MuiTooltip>
                    </TableCell>
                    <TableCell align="right">Earning (P&amp;L)</TableCell>
                    <TableCell align="center">Type</TableCell>
                    <TableCell align="center">Mode</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {equityOrders.map(o => {
                    const isOpen        = o.status === 'PLACED';
                    const isSold        = o.status === 'SOLD';
                    const needsReview   = o.status === 'NEEDS_REVIEW';
                    const entryPrice    = parseFloat(o.ltp || 0);
                    const curPrice      = parseFloat(o.current_price || (isSold ? o.sell_price : entryPrice) || 0);
                    const qty           = parseInt(o.quantity || 1, 10);
                    const pnl           = isOpen
                      ? (o.unrealized_pnl != null ? parseFloat(o.unrealized_pnl) : (curPrice > 0 ? (curPrice - entryPrice) * qty : 0))
                      : parseFloat(o.realized_pnl ?? 0);
                    const pnlColor      = pnl >= 0 ? '#2e7d32' : '#c62828';
                    const investment    = entryPrice * qty;
                    const pnlPct        = investment > 0 ? (pnl / investment) * 100 : 0;
                    return (
                      <TableRow key={o.id} hover sx={{ '& td': { py: 0.6 }, bgcolor: needsReview ? '#fff8e1' : 'inherit' }}>
                        <TableCell>
                          <Typography variant="caption">{dayjs(o.placed_at).format('DD/MM/YY HH:mm')}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight={700} variant="body2">{o.symbol}</Typography>
                          <Typography variant="caption" color="text.secondary">{o.name}</Typography>
                        </TableCell>
                        <TableCell align="right"><b>{o.quantity}</b></TableCell>
                        <TableCell align="right">{entryPrice.toLocaleString('en-IN')}</TableCell>
                        <TableCell align="right">
                          {(isOpen || needsReview)
                            ? (curPrice > 0 ? curPrice.toLocaleString('en-IN') : '—')
                            : isSold ? parseFloat(o.sell_price || 0).toLocaleString('en-IN') : '—'}
                        </TableCell>
                        <TableCell align="right">
                          {isOpen && o.stop_loss ? (
                            <Box>
                              <Typography variant="body2" fontWeight={600} color="error.main">
                                {parseFloat(o.stop_loss).toLocaleString('en-IN')}
                              </Typography>
                              {parseFloat(o.stop_loss) > parseFloat(o.initial_stop_loss) && (
                                <Typography variant="caption" color="success.main">trailing ↑</Typography>
                              )}
                            </Box>
                          ) : needsReview ? (
                            <Typography variant="caption" color="text.secondary">Not armed yet</Typography>
                          ) : isSold && o.exit_reason ? (
                            <Chip size="small" label={o.exit_reason} sx={{ fontSize: 10 }} />
                          ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                        </TableCell>
                        <TableCell align="right">
                          {(isOpen || isSold || needsReview) ? (
                            <Typography fontWeight={700} color={pnlColor}>
                              {pnl >= 0 ? '+' : ''}{pnl.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ({pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(2)}%)
                            </Typography>
                          ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                        </TableCell>
                        <TableCell align="center">
                          <Chip size="small" label={o.product}
                            sx={{ bgcolor: o.product === 'CNC' ? '#e8f5e9' : '#fff8e1',
                                  color: o.product === 'CNC' ? '#2e7d32' : '#e65100',
                                  fontWeight: 700, fontSize: 11 }} />
                        </TableCell>
                        <TableCell align="center">
                          <Chip size="small" label={o.mode || 'LIVE'}
                            sx={{ bgcolor: o.mode === 'PAPER' ? '#e3f2fd' : '#fce4ec',
                                  color: o.mode === 'PAPER' ? '#1565c0' : '#c2185b',
                                  fontWeight: 700, fontSize: 11 }} />
                        </TableCell>
                        <TableCell align="center">
                          <Chip size="small" label={needsReview ? 'NEEDS REVIEW' : o.status}
                            color={isOpen ? 'success' : needsReview ? 'warning' : o.status === 'FAILED' ? 'error' : isSold ? 'default' : 'default'} />
                        </TableCell>
                        <TableCell align="center">
                          {isOpen ? (
                            <Button
                              size="small" variant="contained" color="error"
                              disabled={sellLoadingId === o.id}
                              startIcon={sellLoadingId === o.id ? <CircularProgress size={12} color="inherit" /> : undefined}
                              onClick={() => handleSell(o)}
                              sx={{ fontSize: 11, py: 0.4, px: 1.2, minWidth: 60 }}
                            >
                              Sell
                            </Button>
                          ) : needsReview ? (
                            <Button
                              size="small" variant="outlined" color="warning"
                              disabled={sellLoadingId === o.id}
                              startIcon={sellLoadingId === o.id ? <CircularProgress size={12} color="inherit" /> : undefined}
                              onClick={() => handleActivateMonitoring(o)}
                              sx={{ fontSize: 11, py: 0.4, px: 1.2, minWidth: 60 }}
                            >
                              Activate
                            </Button>
                          ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* ── Reasoning Report Dialog ───────────────────────────────────────── */}
      <Dialog open={reasoningDialog.open} onClose={() => setReasoningDialog({ open: false, text: '', symbol: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>{reasoningDialog.symbol} — Evaluation Report</DialogTitle>
        <DialogContent>
          <Typography component="pre" variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
            {reasoningDialog.text}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReasoningDialog({ open: false, text: '', symbol: '' })}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Test Buy Confirmation Dialog ──────────────────────────────────── */}
      <Dialog open={testBuyDialog.open} onClose={() => !testBuyLoading && setTestBuyDialog({ open: false, symbol: '', direction: '' })} maxWidth="xs" fullWidth>
        <DialogTitle>Test Buy: {testBuyDialog.symbol} {testBuyDialog.direction}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            This places a real order through the bot's execution engine, bypassing the confidence/RR/trend gates — for testing the buy → trailing SL → sell pipeline only.
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#c62828' }}>
            It will execute in whichever mode is currently set in Settings → Trading Mode (PAPER or LIVE). If Trading Mode is PAPER, this is a paper order only — it can never place a real LIVE order while PAPER is selected.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestBuyDialog({ open: false, symbol: '', direction: '' })} disabled={testBuyLoading}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleTestBuy} disabled={testBuyLoading}>
            {testBuyLoading ? 'Placing…' : 'Confirm Buy'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Signal Log — every-minute evaluation log (latest 50) */}
      <Card sx={{ borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Typography variant="h6" fontWeight={600}>Signal Evaluation Log</Typography>
            <Typography variant="caption" color="text.secondary">(every minute · latest 50 entries)</Typography>
          </Box>
          {signalLog.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No evaluations yet — start the bot to begin logging
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 600, bgcolor: '#f5f5f5', py: 0.8 } }}>
                    <TableCell>Time</TableCell>
                    <TableCell>Symbol</TableCell>
                    <TableCell>Direction</TableCell>
                    <TableCell align="right">Spot</TableCell>
                    <TableCell align="center">News</TableCell>
                    <TableCell align="center">Tech</TableCell>
                    <TableCell align="center">RR</TableCell>
                    <TableCell>Result</TableCell>
                    <TableCell>Skip Reason</TableCell>
                    <TableCell align="center">Report</TableCell>
                    <TableCell align="center">Test</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {signalLog.map(s => {
                    const news = parseFloat(s.news_score || 0);
                    const tech = parseFloat(s.tech_score || 0);
                    const rr   = parseFloat(s.rr_ratio   || 0);
                    const resultColor = {
                      TRADED: 'success', SKIPPED: 'default',
                      COOLDOWN: 'warning', DAILY_LIMIT: 'warning',
                      NO_WINDOW: 'default', ERROR: 'error',
                    }[s.result] || 'default';

                    return (
                      <TableRow key={s.id} hover
                        sx={{ '& td': { py: 0.5 }, bgcolor: s.result === 'TRADED' ? '#f1f8e9' : 'inherit' }}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="caption">{dayjs(s.created_at).format('DD/MM HH:mm:ss')}</Typography>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{s.symbol}</TableCell>
                        <TableCell>
                          {s.direction
                            ? <Chip size="small" label={s.direction === 'CALL' ? '▲ CALL' : '▼ PUT'}
                                sx={{ bgcolor: s.direction === 'CALL' ? '#e8f5e9' : '#fce4ec',
                                      color: s.direction === 'CALL' ? '#2e7d32' : '#c62828',
                                      fontWeight: 700, fontSize: 11 }} />
                            : <Typography variant="caption" color="text.disabled">—</Typography>}
                        </TableCell>
                        <TableCell align="right">
                          {s.spot_price > 0
                            ? <Typography variant="caption">₹{parseFloat(s.spot_price).toLocaleString('en-IN')}</Typography>
                            : <Typography variant="caption" color="text.disabled">—</Typography>}
                        </TableCell>
                        <TableCell align="center">
                          {news > 0
                            ? <Chip size="small" label={`${news.toFixed(0)}%`}
                                sx={{ bgcolor: news >= 80 ? '#e8f5e9' : news >= 60 ? '#fff8e1' : '#fce4ec',
                                      color: news >= 80 ? '#2e7d32' : news >= 60 ? '#e65100' : '#c62828',
                                      fontWeight: 600, fontSize: 11 }} />
                            : <Typography variant="caption" color="text.disabled">—</Typography>}
                        </TableCell>
                        <TableCell align="center">
                          {tech > 0
                            ? <Chip size="small" label={`${tech.toFixed(0)}%`}
                                sx={{ bgcolor: tech >= 80 ? '#e8f5e9' : tech >= 60 ? '#fff8e1' : '#fce4ec',
                                      color: tech >= 80 ? '#2e7d32' : tech >= 60 ? '#e65100' : '#c62828',
                                      fontWeight: 600, fontSize: 11 }} />
                            : <Typography variant="caption" color="text.disabled">—</Typography>}
                        </TableCell>
                        <TableCell align="center">
                          {rr > 0
                            ? <Chip size="small" label={`1:${rr.toFixed(1)}`}
                                sx={{ bgcolor: rr >= 2 ? '#e8f5e9' : '#fff8e1',
                                      color: rr >= 2 ? '#2e7d32' : '#e65100',
                                      fontWeight: 600, fontSize: 11 }} />
                            : <Typography variant="caption" color="text.disabled">—</Typography>}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={s.result} color={resultColor} sx={{ fontSize: 11 }} />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 220 }}>
                          <Typography variant="caption" color="text.secondary" noWrap>{s.skip_reason || ''}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          {s.reasoning && (
                            <Button size="small" variant="text" onClick={() => setReasoningDialog({ open: true, text: s.reasoning, symbol: s.symbol })} sx={{ fontSize: 11, minWidth: 0, p: 0.3 }}>
                              View
                            </Button>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {s.direction && (
                            <Button size="small" variant="outlined" color="primary"
                              onClick={() => setTestBuyDialog({ open: true, symbol: s.symbol, direction: s.direction })}
                              sx={{ fontSize: 11, minWidth: 0, px: 1 }}>
                              Buy
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
